'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { IconMapPin, IconMapPinFilled } from '@tabler/icons-react';
import type { GeoLocation } from '@types';
import clsx from 'clsx';
import { debounce } from 'lodash';
import 'mapbox-gl/dist/mapbox-gl.css';
import ReactMap, { Marker, ViewState } from 'react-map-gl/mapbox';
import { WebMercatorViewport } from 'viewport-mercator-project';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

export const MapStyle = {
  standard: 'mapbox://styles/mapbox/standard',
  streets: 'mapbox://styles/mapbox/streets-v12',
  outdoors: 'mapbox://styles/mapbox/outdoors-v12',
  light: 'mapbox://styles/mapbox/light-v11',
  dark: 'mapbox://styles/mapbox/dark-v11',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  'satellite-streets': 'mapbox://styles/mapbox/satellite-streets-v12',
  'navigation-day': 'mapbox://styles/mapbox/navigation-day-v1',
  'navigation-night': 'mapbox://styles/mapbox/navigation-night-v1',
};

export interface MapProps {
  className?: string;
  locations?: (GeoLocation & { primary?: boolean; name?: string })[];
  accessToken?: string;
  onPinClick?: (lat: number, long: number) => void;
  overrideViewState?: {
    latitude: number;
    longitude: number;
    zoom: number;
  };
  zoom?: number;
  bearing?: number;
  pitch?: number;
  style?: keyof typeof MapStyle;
  id?: string;
}

export const Maps = ({
  className,
  locations,
  overrideViewState,
  accessToken,
  onPinClick,
  bearing = 0,
  pitch = 0,
  style = 'standard',
  zoom = 12,
  id,
}: MapProps) => {
  const [viewState, setViewState] = useState({
    width: '100%',
    height: '100%',
    latitude: 0,
    longitude: 0,
    zoom,
    bearing,
    pitch,
  });

  const previousViewState = useRef(viewState);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (Boolean(locations?.length) && isFirstRender.current) {
      const bounds = locations?.reduce(
        (bounds, loc) => ({
          minLng: Math.min(bounds.minLng, loc.longitude),
          minLat: Math.min(bounds.minLat, loc.latitude),
          maxLng: Math.max(bounds.maxLng, loc.longitude),
          maxLat: Math.max(bounds.maxLat, loc.latitude),
        }),
        {
          minLng: Infinity,
          minLat: Infinity,
          maxLng: -Infinity,
          maxLat: -Infinity,
        },
      );

      const viewport = new WebMercatorViewport({
        width: 800,
        height: 600,
      }).fitBounds(
        [
          [bounds?.minLng || 0, bounds?.minLat || 0],
          [bounds?.maxLng || 0, bounds?.maxLat || 0],
        ],
        { padding: 128 },
      );

      setViewState((prevState) => {
        previousViewState.current = {
          ...prevState,
          longitude: viewport.longitude,
          latitude: viewport.latitude,
          zoom: viewport.zoom,
        };
        return previousViewState.current;
      });

      isFirstRender.current = false;
    }
  }, [locations]);

  useEffect(() => {
    if (overrideViewState) {
      setViewState((prevState) => ({
        ...prevState,
        ...overrideViewState,
      }));
    }
  }, [overrideViewState]);

  const handleMove = useCallback(
    debounce((evt) => {
      const { viewState: newViewState } = evt;
      setViewState(newViewState);
    }),
    [viewState],
  );

  return (
    <div
      id={id}
      className={clsx('text-primary relative flex h-full min-h-96 w-full overflow-hidden rounded', className)}
    >
      <ReactMap
        mapStyle={MapStyle[style as keyof typeof MapStyle]}
        maxZoom={15}
        style={{ width: '100%', height: '100%', position: 'absolute' }}
        mapboxAccessToken={accessToken}
        viewState={viewState as unknown as ViewState & { width: number; height: number }}
        onMove={handleMove}
      >
        <TooltipProvider>
          {locations?.map(({ longitude, latitude, primary, name }) => {
            const Icon = primary ? IconMapPinFilled : IconMapPin;

            return (
              <Marker
                key={latitude.toString() + longitude.toString()}
                offset={[0, -16]}
                latitude={latitude}
                onClick={() => onPinClick && onPinClick(latitude, longitude)}
                longitude={longitude}
              >
                <Tooltip>
                  <TooltipContent>{name}</TooltipContent>

                  <TooltipTrigger className="drop-shadow-sm">
                    <Icon size={32} />
                  </TooltipTrigger>
                </Tooltip>
              </Marker>
            );
          })}
        </TooltipProvider>
      </ReactMap>
    </div>
  );
};
